/**
 * Hook para buscar dados do card Campanhas na página Páginas
 */
import { useEffect, useState } from "react";
import { getCampaignsCardPagesApiV1AnalyticsCampaignsPagesGet } from "@/backend-sdk/client/sdk.gen";
import type { CampaignsCardPagesItem } from "@/backend-sdk/client/types.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import type { TableQueryDTO } from "@/types/table-query";

type PagesCampaignData = CampaignsCardPagesItem;

interface CampaignsPaginationState {
  current: number;
  pageSize: number;
  total: number;
}

export const usePagesCampaignsData = (
  startDate: string,
  endDate: string,
  comparison: string,
  customerUuid?: string,
  accountId?: string,
  page: number = 1,
  pageSize: number = 10,
  tableQuery?: TableQueryDTO,
  campaignFiltersNonce?: number,
) => {
  const [data, setData] = useState<PagesCampaignData[]>([]);
  const [total, setTotal] = useState<PagesCampaignData | null>(null);
  const [pagination, setPagination] = useState<CampaignsPaginationState>({
    current: page,
    pageSize,
    total: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!customerUuid) {
        setLoading(false);
        setData([]);
        setTotal(null);
        setPagination({ current: page, pageSize, total: 0 });
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: response } =
          await getCampaignsCardPagesApiV1AnalyticsCampaignsPagesGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page,
              page_size: pageSize,
              q: tableQuery?.q,
              sort_by: tableQuery?.sortBy,
              sort_dir: tableQuery?.sortDir,
              filters: tableQuery?.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
            },
            throwOnError: true,
          });

        setData(response.data ?? []);
        setTotal(response.total ?? null);
        const paginationPayload = response.pagination;
        setPagination({
          current: paginationPayload?.current_page ?? page,
          pageSize: paginationPayload?.page_size ?? pageSize,
          total: paginationPayload?.total_items ?? 0,
        });
      } catch (err: unknown) {
        console.error(
          "Erro ao buscar dados de campanhas (Página Páginas):",
          err,
        );
        setError(
          isHeyApiError(err) && typeof err.detail === "string"
            ? err.detail
            : "Erro ao buscar dados de campanhas",
        );
        setData([]);
        setTotal(null);
        setPagination({ current: page, pageSize, total: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    page,
    pageSize,
    tableQuery,
    campaignFiltersNonce,
  ]);

  return {
    data,
    total,
    pagination,
    loading,
    error,
  };
};
